import type React from 'react'
import { FlipHorizontal2, FlipVertical2 } from 'lucide-react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

import type { CropToolbarHoveredButton } from '../types/crop-toolbar-props'

interface FlipButtonProps {
  hoverKey: Exclude<CropToolbarHoveredButton, 'reset' | null>
  hoveredButton: CropToolbarHoveredButton
  setHoveredButton: (next: CropToolbarHoveredButton) => void
  onClick?: () => void
  icon: React.ReactNode
  label: string
  tooltipContainer: HTMLElement | null
}

function FlipButton({
  hoverKey,
  hoveredButton,
  setHoveredButton,
  onClick,
  icon,
  label,
  tooltipContainer,
}: FlipButtonProps) {
  return (
    <Tooltip open={hoveredButton === hoverKey}>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          onPointerEnter={() => setHoveredButton(hoverKey)}
          onPointerLeave={() => setHoveredButton(null)}
          className="flex h-9 w-9 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent p-0 text-[#1a1a1a] transition-colors hover:bg-black/[0.06]"
        >
          {icon}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" container={tooltipContainer}>
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

interface FlipButtonsProps {
  hoveredButton: CropToolbarHoveredButton
  setHoveredButton: (next: CropToolbarHoveredButton) => void
  onFlipVertical?: () => void
  onFlipHorizontal?: () => void
  tooltipContainer: HTMLElement | null
}

/**
 * Flip horizontal, then flip vertical. Each button's hover key is the OTHER axis's name (the
 * "Flip horizontal" button opens on 'flip-vertical' and vice versa) — carried over verbatim; the
 * keys are only ever compared to each other, so the result is still one tooltip per button.
 */
export function FlipButtons({
  hoveredButton,
  setHoveredButton,
  onFlipVertical,
  onFlipHorizontal,
  tooltipContainer,
}: FlipButtonsProps) {
  return (
    <div className="flex gap-1">
      <FlipButton
        hoverKey="flip-vertical"
        hoveredButton={hoveredButton}
        setHoveredButton={setHoveredButton}
        onClick={onFlipHorizontal}
        icon={<FlipHorizontal2 className="h-4 w-4" />}
        label="Flip horizontal"
        tooltipContainer={tooltipContainer}
      />

      <FlipButton
        hoverKey="flip-horizontal"
        hoveredButton={hoveredButton}
        setHoveredButton={setHoveredButton}
        onClick={onFlipVertical}
        icon={<FlipVertical2 className="h-4 w-4" />}
        label="Flip vertical"
        tooltipContainer={tooltipContainer}
      />
    </div>
  )
}
